import { useState, useCallback } from "react";
import { toast } from "sonner";
import { PDF_MARGIN, saveWithChrome, printChromeStyles, appendPrintChrome } from "@/lib/pdfChrome";

// Encapsulates client-side PDF export AND browser Print of the report
// (report + dashboard KPIs/charts) using the off-screen PrintReport node.
export function usePdfExport(printRef, analytics) {
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);

  const exportPdf = useCallback(async () => {
    if (!analytics) {
      toast.error("No data to export yet.");
      return;
    }
    setExporting(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      // Ensure off-screen charts have rendered before capture.
      await new Promise((r) => setTimeout(r, 500));
      const el = printRef.current;
      if (!el) throw new Error("Nothing to export");
      const opt = {
        margin: PDF_MARGIN,
        filename: `sales-analyst-report-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: 0,
          windowWidth: document.documentElement.offsetWidth,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };
      await saveWithChrome(html2pdf().set(opt).from(el));
      toast.success("PDF exported.");
    } catch (e) {
      toast.error("PDF export failed.");
    } finally {
      setExporting(false);
    }
  }, [printRef, analytics]);

  const printReport = useCallback(async () => {
    if (!analytics) {
      toast.error("No data to print yet.");
      return;
    }
    setPrinting(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      const el = printRef.current;
      if (!el) throw new Error("Nothing to print");

      // Clone the print layout and neutralize its off-screen positioning so it
      // prints on the page (not behind / off-screen).
      const clone = el.cloneNode(true);
      clone.style.position = "static";
      clone.style.left = "0";
      clone.style.top = "0";
      clone.style.zIndex = "auto";
      clone.style.width = "100%";

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(
        `<!DOCTYPE html><html><head><title>Senior Sales Analyst Report</title>` +
          `<meta charset="utf-8" />` +
          `<style>${printChromeStyles()}</style>` +
          `</head><body></body></html>`
      );
      doc.close();
      doc.body.appendChild(doc.importNode(clone, true));
      appendPrintChrome(doc);

      // Give the iframe a tick to lay out, then print.
      await new Promise((r) => setTimeout(r, 350));
      iframe.contentWindow.focus();
      iframe.contentWindow.print();

      // Clean up after the print dialog is handled.
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
      toast.success("Opening print dialog…");
    } catch (e) {
      toast.error("Print failed.");
    } finally {
      setPrinting(false);
    }
  }, [printRef, analytics]);

  return { exporting, exportPdf, printing, printReport };
}
